import { AlertTriangle } from "lucide-react";
import { Link } from "react-router";
import {
  READINESS_LADDER,
  readinessDisplayLabel,
} from "@shared-core/domain/readiness";
import type { ReadinessPipelineEntry } from "@shared-core/data/api-types";
import { paths } from "@shared-core/routing/routes";
import { READINESS_STYLE } from "./ReadinessBadge";

/**
 * Readiness rollup — a single horizontal proportional bar, one segment per
 * readiness value present, ordered by the ladder (upstream to downstream),
 * not by the array's own `job_count` DESC order (see `READINESS_LADDER`).
 * Replaced the earlier five-card layout (2026-09) — one element instead of
 * five reads as one strip rather than a second row of stat tiles competing
 * with `CommandCentreStatStrip` above it.
 *
 * Segment width is proportional to `job_count` via `flexGrow`, with a
 * `min-width` floor (`_SEGMENT_MIN_WIDTH_PX`, set inline rather than via a
 * Tailwind class so it can't be affected by JIT/class-scanning quirks) so a
 * small value next to a large one (a count of 1 beside a count of 40) never
 * collapses to an unreadable sliver — the flex algorithm honours every
 * segment's min-width first and only distributes remaining space by
 * `job_count`. A value with zero jobs never appears at all: the backend's
 * `GROUP BY` only emits rows for readiness values that actually occur, so
 * there is no zero-width segment to special-case here.
 *
 * The key row beneath carries the same information the old cards' second
 * line did (label, overdue_count, longest_days) plus a colour swatch matching
 * its bar segment, so the bar and the key read as one composed unit. Every
 * segment and every key entry links to the jobs page filtered to that
 * readiness value (`?view=work&readiness=<value>` — see `WorkPortfolioPage`'s
 * dismissible readiness chip). Only `not_production_ready` maps to a real
 * board lane on *this* dashboard (`PortfolioBoard` gives it its own section);
 * the other four describe jobs scattered across every deadline lane, so
 * every segment does the same thing — "show me all of these" — via the jobs
 * list. "Jump to my lane" stays on `CommandCentreStatStrip`'s
 * "Not production ready" tile, which is the right home for that affordance.
 *
 * Colours reuse `ReadinessBadge`'s `READINESS_STYLE` (the `readiness` design
 * tokens in `packages/shared-core/src/tokens/source.ts`) — one colour
 * mapping for the ladder, not a second one hand-maintained here. `non_press`
 * has no ladder token (see that file's comment) and renders as a plain
 * neutral segment, matching `ReadinessBadge`'s outline (uncoloured) treatment
 * for the same value.
 */
const _SEGMENT_MIN_WIDTH_PX = 44; // fits a 2-3 digit count at text-sm

function readinessHref(readiness: string): string {
  // `&` not `?`: paths.jobs({ view }) already emits a query string
  // ("/jobs?view=work"), which is that helper's documented contract for
  // callers appending further params. A readiness filter is internal list
  // navigation, so it is built from the route manifest rather than read off
  // the response — unlike an entity's own `_self` link.
  return `${paths.jobs({ view: "work" })}&readiness=${encodeURIComponent(readiness)}`;
}

function ReadinessKeyItem({ entry }: { entry: ReadinessPipelineEntry }) {
  const label = readinessDisplayLabel(entry.readiness);
  const swatchStyle =
    entry.readiness === "non_press"
      ? { backgroundColor: "var(--muted-foreground)" }
      : { backgroundColor: READINESS_STYLE[entry.readiness].backgroundColor };

  return (
    <Link
      to={readinessHref(entry.readiness)}
      className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
    >
      <span className="size-2.5 rounded-full shrink-0" style={swatchStyle} />
      <span className="font-medium text-foreground">{label}</span>
      <span className="tabular-nums">{entry.job_count}</span>
      {entry.overdue_count > 0 && (
        <span className="inline-flex items-center gap-0.5 text-red-600">
          <AlertTriangle className="size-3" />
          {entry.overdue_count} overdue
        </span>
      )}
      {entry.longest_days != null && <span>oldest {entry.longest_days}d</span>}
    </Link>
  );
}

export function CommandCentreReadinessStrip({
  entries,
}: {
  entries: ReadinessPipelineEntry[];
}) {
  const byReadiness = new Map(entries.map((entry) => [entry.readiness, entry]));
  const ordered = READINESS_LADDER.map((readiness) =>
    byReadiness.get(readiness),
  ).filter((entry): entry is ReadinessPipelineEntry => entry !== undefined);

  if (ordered.length === 0) return null;

  return (
    <div className="space-y-2">
      <div className="flex w-full h-9 rounded-lg overflow-hidden border border-border">
        {ordered.map((entry, index) => (
          <Link
            key={entry.readiness}
            to={readinessHref(entry.readiness)}
            title={`${readinessDisplayLabel(entry.readiness)}: ${entry.job_count}`}
            style={{
              flexGrow: entry.job_count,
              flexBasis: 0,
              minWidth: _SEGMENT_MIN_WIDTH_PX,
              ...(entry.readiness === "non_press"
                ? undefined
                : READINESS_STYLE[entry.readiness]),
            }}
            className={`flex items-center justify-center text-sm font-semibold tabular-nums transition-opacity hover:opacity-90 ${
              entry.readiness === "non_press"
                ? "bg-muted text-muted-foreground"
                : ""
            } ${index > 0 ? "border-l border-black/10" : ""}`}
          >
            {entry.job_count}
          </Link>
        ))}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1.5">
        {ordered.map((entry) => (
          <ReadinessKeyItem key={entry.readiness} entry={entry} />
        ))}
      </div>
    </div>
  );
}
